/** Optional, one-shot theme motion (fade/slide-up al scrollear, entrada del hero, vuelo al carrito). Import once with type="module". */
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const seen = new WeakSet();
const pending = new Map();
const running = new Set();
const stickyCleanups = new Map();
const heroCleanups = new Map();
const numberFormat = new Intl.NumberFormat('es-UY', { maximumFractionDigits: 20 });
const duration = 650;

function animate(element, frames, options) {
  if (reduced.matches || !element.animate) return Promise.resolve();
  const animation = element.animate(frames, options);
  running.add(animation);
  return animation.finished.catch(() => {}).finally(() => running.delete(animation));
}

function countup(element) {
  const target = Number(element.dataset.to);
  if (!Number.isFinite(target) || !element.dataset.to?.trim()) return;
  const final = numberFormat.format(target);
  element.textContent = final;
  if (reduced.matches) return;
  // Keep the final accessible value stable while visual digits change.
  const digits = document.createElement('span');
  digits.setAttribute('aria-hidden', 'true');
  const accessible = document.createElement('span');
  accessible.className = 'motion-sr-only';
  accessible.textContent = final;
  element.replaceChildren(digits, accessible);
  const start = performance.now();
  let frame;
  const finish = () => {
    cancelAnimationFrame(frame);
    // A calculator may replace the result while this one-shot effect runs.
    // Preserve external DOM updates and use the latest target when it changes.
    if (element.contains(digits)) {
      const latest = Number(element.dataset.to);
      element.textContent = element.dataset.to?.trim() && Number.isFinite(latest) ? numberFormat.format(latest) : final;
    }
    running.delete(controller);
  };
  const controller = { finish, cancel: finish };
  running.add(controller);
  const tick = now => {
    if (!element.isConnected || reduced.matches || Number(element.dataset.to) !== target || !element.contains(digits)) return finish();
    const progress = Math.min((now - start) / duration, 1);
    const value = target * (1 - (1 - progress) ** 3);
    const decimals = (String(target).split('.')[1] || '').length;
    digits.textContent = numberFormat.format(Number(value.toFixed(decimals)));
    if (progress < 1) frame = requestAnimationFrame(tick);
    else finish();
  };
  frame = requestAnimationFrame(tick);
}

function reveal(element) {
  element.classList.remove('motion-pending');
  pending.delete(element);
  if (element.dataset.motion === 'countup') return countup(element);
  if (reduced.matches) return;
  if (element.dataset.motion === 'reveal') {
    void animate(element, [{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 420, delay: Number(element.dataset.motionDelay || 0), fill: 'backwards', easing: 'cubic-bezier(.2,.8,.2,1)' });
  } else if (element.dataset.motion === 'draw') {
    const shapes = element.matches('svg') ? element.querySelectorAll('path, line, polyline, polygon, circle, rect, ellipse') : element.querySelectorAll('svg path, svg line, svg polyline, svg polygon, svg circle, svg rect, svg ellipse');
    shapes.forEach(shape => {
      try {
        const length = shape.getTotalLength();
        if (length > 0) void animate(shape, [{ strokeDasharray: `${length} ${length}`, strokeDashoffset: length }, { strokeDasharray: `${length} ${length}`, strokeDashoffset: 0 }], { duration, easing: 'ease-out' });
      } catch { /* Non-rendered SVG geometry: keep the original visible. */ }
    });
  }
}

const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
  entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    observer.unobserve(entry.target);
    reveal(entry.target);
  });
}, { threshold: 0, rootMargin: '0px 0px -24px 0px' }) : null;

function elements(root, selector) {
  return [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
}

/** Call for DOM inserted through Section Rendering; initialization is idempotent. */
export function initMotion(root = document) {
  elements(root, '.home-collections > *, .home-steps > *, .rubro-needs > *, .rubro-tile, .tool-card, .home-questions__answers > *, .home-installs__item').forEach((element, index) => {
    if (!element.dataset.motion) element.dataset.motion = 'reveal';
    element.dataset.motionDelay = (index % 4) * 70;
  });
  elements(root, '.catalog__grid .product-card, .home-featured .product-card').forEach((element, index) => {
    element.dataset.motionDelay = (index % 3) * 70;
  });
  elements(root, '.hero, .hero-top').forEach(hero => {
    if (seen.has(hero)) return;
    seen.add(hero);
    hero.querySelectorAll('[data-hero-line]').forEach((line, index) => {
      void animate(line, [{ transform: 'translateY(10px)' }, { transform: 'translateY(0)' }], { duration: 500, delay: index * 90, easing: 'cubic-bezier(.16,1,.3,1)' });
    });
    if (!heroCleanups.has(hero)) heroCleanups.set(hero, initHeroScene(hero));
    const media = hero.querySelector('[data-hero-media]');
    // El escenario (estantería, precio) no se anima al entrar: tiene que verse desde el primer frame.
  });
  elements(root, '[data-motion]').forEach(element => {
    if (seen.has(element)) return;
    seen.add(element);
    if (!observer || reduced.matches) return reveal(element);
    if (element.dataset.motion === 'reveal') element.classList.add('motion-pending');
    pending.set(element, true);
    observer.observe(element);
  });
  elements(root, '[data-sticky-buy]').forEach(bar => {
    if (!stickyCleanups.has(bar)) stickyCleanups.set(bar, initStickyBuy(bar));
  });
}

/** Parallax leve del hero: foto y etiquetas se desplazan con factores distintos mientras el hero está en pantalla. */
function initHeroScene(hero) {
  const controller = new AbortController();
  const { signal } = controller;
  const layers = [...hero.querySelectorAll('[data-parallax]')];
  hero.classList.add('hero--kinetic');
  if (!layers.length) return () => { hero.classList.remove('hero--kinetic'); };
  let frame = 0;
  let visible = true;
  const apply = () => {
    frame = 0;
    if (reduced.matches || !visible) return;
    const offset = Math.min(Math.max(hero.getBoundingClientRect().top * -1, 0), 600);
    layers.forEach(layer => {
      const factor = Number(layer.dataset.parallax) || 0;
      layer.style.setProperty('--parallax', `${(-offset * factor).toFixed(1)}px`);
    });
  };
  const request = () => { if (!frame) frame = requestAnimationFrame(apply); };
  window.addEventListener('scroll', request, { passive: true, signal });
  window.addEventListener('resize', request, { passive: true, signal });
  const visibility = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting;
    hero.classList.toggle('hero--offscreen', !visible);
    if (visible) request();
  }) : null;
  visibility?.observe(hero);
  apply();
  return () => {
    controller.abort();
    visibility?.disconnect();
    cancelAnimationFrame(frame);
    layers.forEach(layer => layer.style.removeProperty('--parallax'));
    hero.classList.remove('hero--kinetic', 'hero--offscreen');
  };
}

/** Sticky bar can target an anchor ID through data-sticky-buy="anchor-id". */
export function initStickyBuy(bar, anchor = null) {
  anchor ||= bar.dataset.stickyBuy ? document.getElementById(bar.dataset.stickyBuy) : document.querySelector('[data-buy-anchor]');
  if (!anchor) return () => {};
  const mobile = window.matchMedia('(max-width: 767px)');
  const originalHidden = bar.hidden;
  const update = () => {
    const rect = anchor.getBoundingClientRect();
    const outside = rect.bottom <= 0;
    bar.hidden = !(mobile.matches && (outside || bar.contains(document.activeElement)));
  };
  const onBlur = () => queueMicrotask(update);
  bar.addEventListener('focusout', onBlur);
  const io = 'IntersectionObserver' in window ? new IntersectionObserver(update) : null;
  io?.observe(anchor);
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  mobile.addEventListener('change', update);
  update();
  return () => {
    io?.disconnect();
    bar.removeEventListener('focusout', onBlur);
    window.removeEventListener('scroll', update);
    window.removeEventListener('resize', update);
    mobile.removeEventListener('change', update);
    bar.hidden = originalHidden;
  };
}

/** Visual feedback only: call AFTER a successful cart mutation. Never changes cart data. */
export async function flyToCart(imgEl) {
  const target = [...document.querySelectorAll('[data-cart-icon]')].find(el => el.getBoundingClientRect().width && el.getBoundingClientRect().height);
  if (reduced.matches || !imgEl?.isConnected || !target || !imgEl.animate) return;
  const from = imgEl.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  if (!from.width || !from.height || to.bottom <= 0 || to.top >= innerHeight) return;
  const clone = imgEl.cloneNode(false);
  clone.removeAttribute('id');
  clone.removeAttribute('srcset');
  clone.removeAttribute('sizes');
  clone.src = imgEl.currentSrc || imgEl.src;
  clone.alt = '';
  clone.setAttribute('aria-hidden', 'true');
  clone.className = 'motion-flight';
  Object.assign(clone.style, { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px` });
  document.body.append(clone);
  try {
    await animate(clone, [{ transform: 'translate(0, 0) scale(1)', opacity: 0.9 }, { transform: `translate(${to.left + to.width / 2 - from.left - from.width / 2}px, ${to.top + to.height / 2 - from.top - from.height / 2}px) scale(0.12)`, opacity: 0.2 }], { duration: 500, easing: 'cubic-bezier(.2,.7,.3,1)' });
  } finally {
    clone.remove();
  }
  const counter = document.querySelector('[data-cart-count]') || target;
  await animate(counter, [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 200, easing: 'ease-out' });
}

reduced.addEventListener('change', () => {
  if (!reduced.matches) return;
  running.forEach(animation => animation.cancel());
  pending.forEach((_, element) => { observer?.unobserve(element); reveal(element); });
});
document.addEventListener('shopify:section:load', event => initMotion(event.target));
document.addEventListener('shopify:section:unload', event => {
  pending.forEach((_, element) => {
    if (event.target.contains(element)) { observer?.unobserve(element); pending.delete(element); }
  });
  heroCleanups.forEach((cleanup, hero) => {
    if (event.target.contains(hero)) { cleanup(); heroCleanups.delete(hero); }
  });
  stickyCleanups.forEach((cleanup, bar) => {
    if (event.target.contains(bar)) { cleanup(); stickyCleanups.delete(bar); }
  });
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initMotion(), { once: true });
else initMotion();
